/**
 * Component: PublicRegistrationLinkCard
 * Shareable link + QR (for posters / chats) to the public pre-registration form.
 */

import { AlertTriangle, CheckCircle2, Copy, ExternalLink, Link2, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ReceiptQrCode } from '@/features/receipts/components/ReceiptQrCode';
import type { Event, EventStatus } from '@/types/event.types';
import { registrationUrl } from '../public/publicUrls';
import { NO_PERMISSION_HINT, statusActionLabel } from '../utils/statusActions';

interface PublicRegistrationLinkCardProps {
  event: Event;
  canTransition: boolean;
  onRequestStatus: (target: EventStatus) => void;
}

/** Says whether the public form accepts registrations right now, and offers the action to change it */
function RegistrationStateBanner({ event, canTransition, onRequestStatus }: PublicRegistrationLinkCardProps) {
  const status = event.status as EventStatus;
  const deadline = event.registration_deadline ? new Date(event.registration_deadline) : null;
  const deadlinePassed = !!deadline && deadline.getTime() <= Date.now();

  const action = (target: EventStatus, variant: 'default' | 'outline') => (
    <span title={canTransition ? undefined : NO_PERMISSION_HINT}>
      <Button size="sm" variant={variant} disabled={!canTransition} onClick={() => onRequestStatus(target)}>
        {statusActionLabel(status, target)}
      </Button>
    </span>
  );

  const box = (tone: 'ok' | 'warn' | 'muted', Icon: typeof AlertTriangle, text: string, button?: React.ReactNode) => (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
        tone === 'ok'
          ? 'border-green-200 bg-green-50 text-green-900'
          : tone === 'warn'
            ? 'border-amber-300 bg-amber-50 text-amber-900'
            : 'border-border bg-muted/50 text-muted-foreground'
      }`}
    >
      <span className="flex items-center gap-2">
        <Icon className="size-4 shrink-0" />
        {text}
      </span>
      {button}
    </div>
  );

  if (status === 'DRAFT') {
    return box('warn', AlertTriangle, 'Inscripciones no abiertas: el formulario público está cerrado (evento en borrador).', action('OPEN', 'default'));
  }
  if (status === 'CLOSED') {
    return box('warn', AlertTriangle, 'Inscripciones cerradas: el formulario público no acepta nuevos registros.', action('OPEN', 'outline'));
  }
  if (status === 'COMPLETED') {
    return box('muted', Lock, 'Evento completado: el formulario público está cerrado.');
  }
  if (deadlinePassed) {
    return box(
      'warn',
      AlertTriangle,
      `Plazo vencido el ${deadline.toLocaleString('es-BO')}: aunque el evento está abierto, el formulario público ya no acepta inscripciones. Edite el evento para ampliar el plazo.`,
    );
  }
  return box(
    'ok',
    CheckCircle2,
    deadline
      ? `Inscripciones abiertas hasta el ${deadline.toLocaleString('es-BO')}.`
      : 'Inscripciones abiertas, sin fecha límite.',
    action('CLOSED', 'outline'),
  );
}

export function PublicRegistrationLinkCard({ event, canTransition, onRequestStatus }: PublicRegistrationLinkCardProps) {
  const { toast } = useToast();
  const url = registrationUrl(event.code);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Enlace copiado' });
    } catch {
      toast({ title: 'No se pudo copiar el enlace', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="size-5" />
          Inscripción pública
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <RegistrationStateBanner event={event} canTransition={canTransition} onRequestStatus={onRequestStatus} />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <ReceiptQrCode url={url} size={112} className="shrink-0 self-center rounded border" />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm text-muted-foreground">
              Comparta este enlace o QR para que ministros y delegados se preinscriban sin cuenta.
            </p>
            <div className="flex gap-2">
              <Input value={url} readOnly className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button variant="outline" size="icon" onClick={copy} aria-label="Copiar enlace">
                <Copy className="size-4" />
              </Button>
              <Button variant="outline" size="icon" asChild aria-label="Abrir formulario">
                <a href={url} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
